import {
  createIdentityImageEdit,
  isIdentityImageEdit,
  projectSourcePointToCrop,
  unprojectCropPointToSource,
} from '@buffer-mono/popcorn'
import type {
  ImageDimensions,
  ImageEdit,
  NormalizedImagePoint,
} from '@buffer-mono/popcorn'

import type {
  Image,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'

/**
 * Narrows a media reference to `Video`. Checks a field unique to `Video`
 * rather than object identity, so it works for any video, not only the one
 * currently held in a session.
 */
export const isVideoMedia = (media: Image | Video): media is Video =>
  'duration' in media

export type ImageWorkspaceUserTag = Readonly<{
  username: string
  x: string
  y: string
}>

export type ImageWorkspaceUserTagInput = Readonly<{
  username: string
  point: NormalizedImagePoint
}>

export type ImageWorkspaceUserTagAnchor =
  | Readonly<{
      state: 'valid'
      username: string
      sourcePoint: NormalizedImagePoint
      initialUserTag?: ImageWorkspaceUserTag
    }>
  | Readonly<{
      state: 'invalid'
      userTag: ImageWorkspaceUserTag
    }>

export type ImageWorkspaceUserTagProjection =
  | Readonly<{
      state: 'inside'
      anchorIndex: number
      username: string
      imagePoint: NormalizedImagePoint
      point: NormalizedImagePoint
    }>
  | Readonly<{
      state: 'outside'
      anchorIndex: number
      username: string
      imagePoint?: NormalizedImagePoint
    }>
  | Readonly<{
      state: 'invalid'
      anchorIndex: number
      username: string
    }>

export type ImageWorkspaceItem = Readonly<{
  originalImage: Image
  initialAltText: string
  initialUserTagAnchors: readonly ImageWorkspaceUserTagAnchor[]
  sourceDimensions: ImageDimensions | null
  edit: ImageEdit
  altText: string
  userTagAnchors: readonly ImageWorkspaceUserTagAnchor[]
}>

/** A captured frame staged as the video's new thumbnail, not yet uploaded. */
export type VideoThumbnailSelection = Readonly<{
  offsetMs: number
  blob: Blob
}>

/**
 * A trailing video attachment. Video has no crop/tag/alt text editing, only
 * a thumbnail frame and a title, both optional and independent of one
 * another.
 */
export type ImageWorkspaceVideoItem = Readonly<{
  originalVideo: Video
  thumbnailSelection: VideoThumbnailSelection | null
  title: string
}>

/**
 * `items` holds only image items. Every attached video sorts after every
 * image (see `assetListOps.insertAtCanonicalPosition`), so `videos` is
 * modeled as the implicit trailing run starting at `items.length`: video
 * index `j` sits at `items.length + j`. `activeIndex` ranges over
 * `[0, items.length + videos.length)`; see `getActiveVideoIndex`.
 */
export type ImageWorkspaceSession = Readonly<{
  activeIndex: number
  items: readonly ImageWorkspaceItem[]
  videos: readonly ImageWorkspaceVideoItem[]
}>

export type ImageWorkspaceChangeSummary = Readonly<{
  changedImageCount: number
  visualEditImageCount: number
  altTextChangeImageCount: number
  userTagEditImageCount: number
  userTagOutputChangeImageCount: number
  hasCropChange: boolean
  hasRotationChange: boolean
  hasHorizontalFlipChange: boolean
  hasVerticalFlipChange: boolean
  hasThumbnailChange: boolean
  hasTitleChange: boolean
}>

type ImageWorkspaceResultItem = Readonly<{
  originalImage: Image
  edit: ImageEdit
  altText: string
  userTags: readonly ImageWorkspaceUserTag[]
}>

export type ImageWorkspaceResult = Readonly<{
  items: readonly ImageWorkspaceResultItem[]
  /** One entry per video the user staged a new thumbnail frame for. */
  videoThumbnails?: readonly Readonly<{
    video: Video
    offsetMs: number
    blob: Blob
  }>[]
  /** One entry per video whose title differs from its original value. */
  videoTitles?: readonly Readonly<{
    video: Video
    title: string
  }>[]
}>

export type ImageWorkspaceResultState =
  | Readonly<{
      state: 'ready'
      result: ImageWorkspaceResult
    }>
  | Readonly<{
      state: 'blocked'
      imageIndexes: readonly number[]
    }>

type ImageWorkspaceSessionAction =
  | Readonly<{
      type: 'select-image'
      image: Image
    }>
  | Readonly<{
      type: 'select-video'
      video: Video
    }>
  | Readonly<{
      type: 'set-edit'
      image: Image
      edit: ImageEdit
    }>
  | Readonly<{
      type: 'set-source-dimensions'
      image: Image
      sourceDimensions: ImageDimensions
    }>
  | Readonly<{
      type: 'set-alt-text'
      image: Image
      altText: string
    }>
  | Readonly<{
      type: 'add-user-tag'
      image: Image
      tag: ImageWorkspaceUserTagInput
    }>
  | Readonly<{
      type: 'move-user-tag'
      image: Image
      anchorIndex: number
      point: NormalizedImagePoint
    }>
  | Readonly<{
      type: 'remove-user-tag'
      image: Image
      anchorIndex: number
    }>
  | Readonly<{
      type: 'set-video-thumbnail-selection'
      video: Video
      selection: VideoThumbnailSelection
    }>
  | Readonly<{
      type: 'set-video-title'
      video: Video
      title: string
    }>
  | Readonly<{
      type: 'sync-videos'
      videos: readonly Video[]
    }>

type CreateImageWorkspaceSessionOptions = Readonly<{
  images: readonly Readonly<{
    image: Image
    sourceDimensions: ImageDimensions | null
  }>[]
  videos: readonly Video[]
  initialMedia: Image | Video
}>

const cloneUserTag = (
  userTag: ImageWorkspaceUserTag,
): ImageWorkspaceUserTag => ({
  username: userTag.username,
  x: userTag.x,
  y: userTag.y,
})

const cloneImageEdit = (edit: ImageEdit): ImageEdit => ({
  ...edit,
  crop: { ...edit.crop },
})

const getImageDimensions = (
  width: number | null | undefined,
  height: number | null | undefined,
): ImageDimensions | null =>
  typeof width === 'number' &&
  Number.isFinite(width) &&
  width > 0 &&
  typeof height === 'number' &&
  Number.isFinite(height) &&
  height > 0
    ? { width, height }
    : null

const parseNormalizedCoordinate = (value: string): number | null => {
  if (value.trim() === '') return null

  const coordinate = Number(value)
  return Number.isFinite(coordinate) && coordinate >= 0 && coordinate <= 1
    ? coordinate
    : null
}

export const parseImageWorkspaceUserTagPoint = (
  userTag: ImageWorkspaceUserTag,
): NormalizedImagePoint | null => {
  const x = parseNormalizedCoordinate(userTag.x)
  const y = parseNormalizedCoordinate(userTag.y)

  return x === null || y === null ? null : { x, y }
}

const createUserTagAnchor = (
  userTag: ImageWorkspaceUserTag,
): ImageWorkspaceUserTagAnchor => {
  const clonedUserTag = cloneUserTag(userTag)
  const sourcePoint = parseImageWorkspaceUserTagPoint(clonedUserTag)

  return sourcePoint === null
    ? { state: 'invalid', userTag: clonedUserTag }
    : {
        state: 'valid',
        username: clonedUserTag.username,
        sourcePoint,
        initialUserTag: clonedUserTag,
      }
}

const areImageEditsEqual = (first: ImageEdit, second: ImageEdit): boolean =>
  (isIdentityImageEdit(first) && isIdentityImageEdit(second)) ||
  (first.crop.x === second.crop.x &&
    first.crop.y === second.crop.y &&
    first.crop.width === second.crop.width &&
    first.crop.height === second.crop.height &&
    first.rotation === second.rotation &&
    first.straightening === second.straightening &&
    first.flipHorizontal === second.flipHorizontal &&
    first.flipVertical === second.flipVertical &&
    first.brightness === second.brightness &&
    first.contrast === second.contrast &&
    first.saturation === second.saturation &&
    first.warmth === second.warmth &&
    first.filterPreset === second.filterPreset &&
    first.filterIntensity === second.filterIntensity)

const hasImageGeometryEdit = (edit: ImageEdit): boolean =>
  !isIdentityImageEdit({
    ...edit,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    warmth: 0,
    filterPreset: 'none',
  })

const areUserTagsEqual = (
  first: ImageWorkspaceUserTag,
  second: ImageWorkspaceUserTag,
): boolean =>
  first.username === second.username &&
  first.x === second.x &&
  first.y === second.y

const areOptionalUserTagsEqual = (
  first: ImageWorkspaceUserTag | undefined,
  second: ImageWorkspaceUserTag | undefined,
): boolean =>
  first === undefined
    ? second === undefined
    : second !== undefined && areUserTagsEqual(first, second)

const NORMALIZED_POINT_TOLERANCE = 1e-9

const areNormalizedPointsEqual = (
  first: NormalizedImagePoint,
  second: NormalizedImagePoint,
): boolean =>
  Math.abs(first.x - second.x) <= NORMALIZED_POINT_TOLERANCE &&
  Math.abs(first.y - second.y) <= NORMALIZED_POINT_TOLERANCE

const areUserTagAnchorsEqual = (
  first: readonly ImageWorkspaceUserTagAnchor[],
  second: readonly ImageWorkspaceUserTagAnchor[],
): boolean =>
  first.length === second.length &&
  first.every((anchor, index) => {
    const otherAnchor = second[index]
    if (otherAnchor === undefined || anchor.state !== otherAnchor.state) {
      return false
    }

    if (anchor.state === 'invalid' && otherAnchor.state === 'invalid') {
      return areUserTagsEqual(anchor.userTag, otherAnchor.userTag)
    }

    return (
      anchor.state === 'valid' &&
      otherAnchor.state === 'valid' &&
      anchor.username === otherAnchor.username &&
      anchor.sourcePoint.x === otherAnchor.sourcePoint.x &&
      anchor.sourcePoint.y === otherAnchor.sourcePoint.y &&
      areOptionalUserTagsEqual(
        anchor.initialUserTag,
        otherAnchor.initialUserTag,
      )
    )
  })

const createImageWorkspaceItem = ({
  image,
  sourceDimensions,
}: CreateImageWorkspaceSessionOptions['images'][number]): ImageWorkspaceItem => {
  const initialAltText = image.altText ?? ''
  const initialUserTagAnchors = (image.userTags ?? []).map(createUserTagAnchor)

  return {
    originalImage: image,
    initialAltText,
    initialUserTagAnchors,
    sourceDimensions:
      sourceDimensions ?? getImageDimensions(image.width, image.height),
    edit: createIdentityImageEdit(),
    altText: initialAltText,
    userTagAnchors: initialUserTagAnchors,
  }
}

const createVideoWorkspaceItem = (video: Video): ImageWorkspaceVideoItem => ({
  originalVideo: video,
  thumbnailSelection: null,
  title: video.name,
})

export const createImageWorkspaceSession = ({
  images,
  videos,
  initialMedia,
}: CreateImageWorkspaceSessionOptions): ImageWorkspaceSession => {
  const imageIndex = images.findIndex(({ image }) => image === initialMedia)
  const videoIndex = videos.findIndex((video) => video === initialMedia)
  const activeIndex =
    imageIndex !== -1
      ? imageIndex
      : videoIndex !== -1
        ? images.length + videoIndex
        : -1

  if (activeIndex === -1) {
    throw new Error(
      'The initial image or video must be included in the workspace',
    )
  }

  return {
    activeIndex,
    items: images.map(createImageWorkspaceItem),
    videos: videos.map(createVideoWorkspaceItem),
  }
}

/**
 * The active video's index into `session.videos`, or `null` when an image is
 * active — `activeIndex` sits at `items.length + j` for video index `j`.
 */
export const getActiveVideoIndex = (
  session: ImageWorkspaceSession,
): number | null => {
  const videoIndex = session.activeIndex - session.items.length
  return videoIndex >= 0 && videoIndex < session.videos.length
    ? videoIndex
    : null
}

/** Whether a video (any video) is the active item. */
export const isImageWorkspaceVideoActive = (
  session: ImageWorkspaceSession,
): boolean => getActiveVideoIndex(session) !== null

/** Total navigable items: image items plus every attached video. */
export const getImageWorkspaceItemCount = (
  session: ImageWorkspaceSession,
): number => session.items.length + session.videos.length

const updateImageWorkspaceItem = (
  session: ImageWorkspaceSession,
  image: Image,
  update: (item: ImageWorkspaceItem) => ImageWorkspaceItem,
): ImageWorkspaceSession => {
  const itemIndex = session.items.findIndex(
    (item) => item.originalImage === image,
  )
  const currentItem = session.items[itemIndex]

  if (currentItem === undefined) return session

  const nextItem = update(currentItem)
  if (nextItem === currentItem) return session

  return {
    ...session,
    items: session.items.map((item, index) =>
      index === itemIndex ? nextItem : item,
    ),
  }
}

const selectImage = (
  session: ImageWorkspaceSession,
  image: Image,
): ImageWorkspaceSession => {
  const nextActiveIndex = session.items.findIndex(
    (item) => item.originalImage === image,
  )

  if (nextActiveIndex === -1 || nextActiveIndex === session.activeIndex) {
    return session
  }

  return { ...session, activeIndex: nextActiveIndex }
}

const setEdit = (
  session: ImageWorkspaceSession,
  image: Image,
  edit: ImageEdit,
): ImageWorkspaceSession =>
  updateImageWorkspaceItem(session, image, (item) =>
    areImageEditsEqual(item.edit, edit)
      ? item
      : { ...item, edit: cloneImageEdit(edit) },
  )

const setSourceDimensions = (
  session: ImageWorkspaceSession,
  image: Image,
  sourceDimensions: ImageDimensions,
): ImageWorkspaceSession =>
  updateImageWorkspaceItem(session, image, (item) =>
    item.sourceDimensions?.width === sourceDimensions.width &&
    item.sourceDimensions?.height === sourceDimensions.height
      ? item
      : { ...item, sourceDimensions: { ...sourceDimensions } },
  )

const getItemSourceDimensions = (item: ImageWorkspaceItem): ImageDimensions => {
  if (item.sourceDimensions) return item.sourceDimensions
  if (item.edit.straightening !== 0) {
    throw new Error('A straightened image must have source dimensions')
  }

  return { width: 1, height: 1 }
}

const setAltText = (
  session: ImageWorkspaceSession,
  image: Image,
  altText: string,
): ImageWorkspaceSession =>
  updateImageWorkspaceItem(session, image, (item) =>
    item.altText === altText ? item : { ...item, altText },
  )

const addUserTag = (
  session: ImageWorkspaceSession,
  image: Image,
  tag: ImageWorkspaceUserTagInput,
): ImageWorkspaceSession =>
  updateImageWorkspaceItem(session, image, (item) => ({
    ...item,
    userTagAnchors: [
      ...item.userTagAnchors,
      {
        state: 'valid',
        username: tag.username,
        sourcePoint: unprojectCropPointToSource(
          tag.point,
          item.edit,
          getItemSourceDimensions(item),
        ),
      },
    ],
  }))

const removeUserTag = (
  session: ImageWorkspaceSession,
  image: Image,
  anchorIndex: number,
): ImageWorkspaceSession =>
  updateImageWorkspaceItem(session, image, (item) =>
    item.userTagAnchors[anchorIndex] === undefined
      ? item
      : {
          ...item,
          userTagAnchors: item.userTagAnchors.filter(
            (_, index) => index !== anchorIndex,
          ),
        },
  )

const moveUserTag = (
  session: ImageWorkspaceSession,
  image: Image,
  anchorIndex: number,
  point: NormalizedImagePoint,
): ImageWorkspaceSession =>
  updateImageWorkspaceItem(session, image, (item) => {
    const currentAnchor = item.userTagAnchors[anchorIndex]
    if (currentAnchor === undefined || currentAnchor.state !== 'valid') {
      return item
    }

    const sourcePoint = unprojectCropPointToSource(
      point,
      item.edit,
      getItemSourceDimensions(item),
    )
    if (areNormalizedPointsEqual(currentAnchor.sourcePoint, sourcePoint)) {
      return item
    }

    const movedAnchor = {
      ...currentAnchor,
      sourcePoint,
    }

    return {
      ...item,
      userTagAnchors: item.userTagAnchors.map((anchor, index) =>
        index === anchorIndex ? movedAnchor : anchor,
      ),
    }
  })

const updateVideoWorkspaceItem = (
  session: ImageWorkspaceSession,
  video: Video,
  update: (item: ImageWorkspaceVideoItem) => ImageWorkspaceVideoItem,
): ImageWorkspaceSession => {
  const videoIndex = session.videos.findIndex(
    (item) => item.originalVideo === video,
  )
  const currentItem = session.videos[videoIndex]

  if (currentItem === undefined) return session

  const nextItem = update(currentItem)
  if (nextItem === currentItem) return session

  return {
    ...session,
    videos: session.videos.map((item, index) =>
      index === videoIndex ? nextItem : item,
    ),
  }
}

const setVideoThumbnailSelection = (
  session: ImageWorkspaceSession,
  video: Video,
  selection: VideoThumbnailSelection,
): ImageWorkspaceSession =>
  updateVideoWorkspaceItem(session, video, (item) => ({
    ...item,
    thumbnailSelection: selection,
  }))

const setVideoTitle = (
  session: ImageWorkspaceSession,
  video: Video,
  title: string,
): ImageWorkspaceSession =>
  updateVideoWorkspaceItem(session, video, (item) =>
    item.title === title ? item : { ...item, title },
  )

const selectVideo = (
  session: ImageWorkspaceSession,
  video: Video,
): ImageWorkspaceSession => {
  const videoIndex = session.videos.findIndex(
    (item) => item.originalVideo === video,
  )
  if (videoIndex === -1) return session

  const nextActiveIndex = session.items.length + videoIndex
  return nextActiveIndex === session.activeIndex
    ? session
    : { ...session, activeIndex: nextActiveIndex }
}

/**
 * Re-derives `session.videos` from the caller's live video list, matched to
 * existing items by URL (the stable identity across a same-slot replacement
 * — e.g. a single-video network attaching a new upload over the old one)
 * rather than by object reference, so a video object swapped out from under
 * an open session doesn't silently make its own filmstrip tab unselectable
 * or read as "no longer attached". A matched item's staged thumbnail/title
 * edit survives; an unmatched incoming video starts a fresh item.
 */
const syncVideos = (
  session: ImageWorkspaceSession,
  videos: readonly Video[],
): ImageWorkspaceSession => {
  const existingByUrl = new Map(
    session.videos.map((item) => [item.originalVideo.url, item] as const),
  )
  const nextVideos = videos.map(
    (video) => existingByUrl.get(video.url) ?? createVideoWorkspaceItem(video),
  )

  const unchanged =
    nextVideos.length === session.videos.length &&
    nextVideos.every((item, index) => item === session.videos[index])
  if (unchanged) return session

  const activeVideoIndex = getActiveVideoIndex(session)
  const activeUrl =
    activeVideoIndex !== null
      ? session.videos[activeVideoIndex]?.originalVideo.url
      : undefined
  const matchedActiveIndex =
    activeUrl !== undefined
      ? nextVideos.findIndex((item) => item.originalVideo.url === activeUrl)
      : -1
  const activeIndex =
    matchedActiveIndex !== -1
      ? session.items.length + matchedActiveIndex
      : Math.min(
          session.activeIndex,
          session.items.length + nextVideos.length - 1,
        )

  return {
    ...session,
    videos: nextVideos,
    activeIndex: Math.max(0, activeIndex),
  }
}

export const imageWorkspaceSessionReducer = (
  session: ImageWorkspaceSession,
  action: ImageWorkspaceSessionAction,
): ImageWorkspaceSession => {
  switch (action.type) {
    case 'select-image':
      return selectImage(session, action.image)
    case 'select-video':
      return selectVideo(session, action.video)
    case 'set-edit':
      return setEdit(session, action.image, action.edit)
    case 'set-source-dimensions':
      return setSourceDimensions(session, action.image, action.sourceDimensions)
    case 'set-alt-text':
      return setAltText(session, action.image, action.altText)
    case 'add-user-tag':
      return addUserTag(session, action.image, action.tag)
    case 'move-user-tag':
      return moveUserTag(
        session,
        action.image,
        action.anchorIndex,
        action.point,
      )
    case 'remove-user-tag':
      return removeUserTag(session, action.image, action.anchorIndex)
    case 'set-video-thumbnail-selection':
      return setVideoThumbnailSelection(session, action.video, action.selection)
    case 'set-video-title':
      return setVideoTitle(session, action.video, action.title)
    case 'sync-videos':
      return syncVideos(session, action.videos)
  }
}

export const getImageWorkspaceItemAt = (
  session: ImageWorkspaceSession,
  index: number,
): ImageWorkspaceItem => {
  const item = session.items[index]
  if (item === undefined) {
    throw new Error('The workspace has no item at the requested index')
  }

  return item
}

/**
 * Throws when a video is active — callers must check
 * `isImageWorkspaceVideoActive` first and use `getActiveVideoWorkspaceItem`
 * instead.
 */
export const getActiveImageWorkspaceItem = (
  session: ImageWorkspaceSession,
): ImageWorkspaceItem => {
  const activeItem = session.items[session.activeIndex]
  if (activeItem === undefined) {
    throw new Error('The workspace has no active image')
  }

  return activeItem
}

/**
 * Throws when no video is active — callers must check
 * `getActiveVideoIndex`/`isImageWorkspaceVideoActive` first.
 */
export const getActiveVideoWorkspaceItem = (
  session: ImageWorkspaceSession,
): ImageWorkspaceVideoItem => {
  const videoIndex = getActiveVideoIndex(session)
  const activeItem =
    videoIndex === null ? undefined : session.videos[videoIndex]
  if (activeItem === undefined) {
    throw new Error('The workspace has no active video')
  }

  return activeItem
}

export const getImageWorkspaceUserTagProjections = (
  item: ImageWorkspaceItem,
): readonly ImageWorkspaceUserTagProjection[] => {
  const fullImageEdit = {
    ...item.edit,
    crop: createIdentityImageEdit().crop,
  }
  const sourceDimensions = getItemSourceDimensions(item)

  return item.userTagAnchors.map((anchor, anchorIndex) => {
    if (anchor.state === 'invalid') {
      return {
        state: 'invalid',
        anchorIndex,
        username: anchor.userTag.username,
      }
    }

    const imageProjection = projectSourcePointToCrop(
      anchor.sourcePoint,
      fullImageEdit,
      sourceDimensions,
    )
    const projection = projectSourcePointToCrop(
      anchor.sourcePoint,
      item.edit,
      sourceDimensions,
    )
    if (imageProjection.status === 'outside') {
      return {
        state: 'outside',
        anchorIndex,
        username: anchor.username,
      }
    }

    return projection.status === 'inside'
      ? {
          state: 'inside',
          anchorIndex,
          username: anchor.username,
          imagePoint: imageProjection.point,
          point: projection.point,
        }
      : {
          state: 'outside',
          anchorIndex,
          username: anchor.username,
          imagePoint: imageProjection.point,
        }
  })
}

export const isImageWorkspaceItemDirty = (item: ImageWorkspaceItem): boolean =>
  !isIdentityImageEdit(item.edit) ||
  item.altText !== item.initialAltText ||
  !areUserTagAnchorsEqual(item.userTagAnchors, item.initialUserTagAnchors)

const isVideoWorkspaceItemDirty = (item: ImageWorkspaceVideoItem): boolean =>
  item.thumbnailSelection !== null || item.title !== item.originalVideo.name

export const isImageWorkspaceVideoDirty = (
  session: ImageWorkspaceSession,
): boolean => session.videos.some(isVideoWorkspaceItemDirty)

/** Count of attached videos with a staged thumbnail or title change. */
export const getDirtyVideoCount = (session: ImageWorkspaceSession): number =>
  session.videos.filter(isVideoWorkspaceItemDirty).length

export const isImageWorkspaceSessionDirty = (
  session: ImageWorkspaceSession,
): boolean =>
  session.items.some(isImageWorkspaceItemDirty) ||
  isImageWorkspaceVideoDirty(session)

export const getImageWorkspaceStagedUserTagCount = (
  session: ImageWorkspaceSession,
): number =>
  session.items.reduce((total, item) => total + item.userTagAnchors.length, 0)

const getResultUserTags = (
  item: ImageWorkspaceItem,
): readonly ImageWorkspaceUserTag[] => {
  const preserveSourceTags = !hasImageGeometryEdit(item.edit)
  const userTags: ImageWorkspaceUserTag[] = []

  for (const anchor of item.userTagAnchors) {
    if (anchor.state === 'invalid') {
      if (preserveSourceTags) userTags.push(cloneUserTag(anchor.userTag))
      continue
    }

    const initialPoint = anchor.initialUserTag
      ? parseImageWorkspaceUserTagPoint(anchor.initialUserTag)
      : null
    if (
      preserveSourceTags &&
      anchor.initialUserTag &&
      initialPoint &&
      areNormalizedPointsEqual(anchor.sourcePoint, initialPoint)
    ) {
      userTags.push(cloneUserTag(anchor.initialUserTag))
      continue
    }

    const projection = projectSourcePointToCrop(
      anchor.sourcePoint,
      item.edit,
      getItemSourceDimensions(item),
    )
    if (projection.status === 'outside') continue

    userTags.push({
      username: anchor.username,
      x: projection.point.x.toFixed(2),
      y: projection.point.y.toFixed(2),
    })
  }

  return userTags
}

const getInitialUserTags = (
  item: ImageWorkspaceItem,
): readonly ImageWorkspaceUserTag[] => {
  const userTags: ImageWorkspaceUserTag[] = []

  for (const anchor of item.initialUserTagAnchors) {
    if (anchor.state === 'invalid') {
      userTags.push(anchor.userTag)
    } else if (anchor.initialUserTag) {
      userTags.push(anchor.initialUserTag)
    }
  }

  return userTags
}

const areUserTagListsEqual = (
  first: readonly ImageWorkspaceUserTag[],
  second: readonly ImageWorkspaceUserTag[],
): boolean =>
  first.length === second.length &&
  first.every((userTag, index) => {
    const otherUserTag = second[index]
    return otherUserTag !== undefined && areUserTagsEqual(userTag, otherUserTag)
  })

const hasCropChange = (edit: ImageEdit): boolean =>
  !isIdentityImageEdit({
    ...createIdentityImageEdit(),
    crop: edit.crop,
  })

export const getImageWorkspaceChangeSummary = (
  session: ImageWorkspaceSession,
): ImageWorkspaceChangeSummary => {
  let changedImageCount = 0
  let visualEditImageCount = 0
  let altTextChangeImageCount = 0
  let userTagEditImageCount = 0
  let userTagOutputChangeImageCount = 0
  let sessionHasCropChange = false
  let hasRotationChange = false
  let hasHorizontalFlipChange = false
  let hasVerticalFlipChange = false

  for (const item of session.items) {
    if (isImageWorkspaceItemDirty(item)) changedImageCount += 1

    const hasVisualEdit = !isIdentityImageEdit(item.edit)
    if (hasVisualEdit) {
      visualEditImageCount += 1
      sessionHasCropChange ||= hasCropChange(item.edit)
      hasRotationChange ||=
        item.edit.rotation !== 0 || item.edit.straightening !== 0
      hasHorizontalFlipChange ||= item.edit.flipHorizontal
      hasVerticalFlipChange ||= item.edit.flipVertical
    }

    if (item.altText !== item.initialAltText) altTextChangeImageCount += 1

    if (
      !areUserTagAnchorsEqual(item.userTagAnchors, item.initialUserTagAnchors)
    ) {
      userTagEditImageCount += 1
    }

    if (
      !areUserTagListsEqual(getResultUserTags(item), getInitialUserTags(item))
    ) {
      userTagOutputChangeImageCount += 1
    }
  }

  return {
    changedImageCount,
    visualEditImageCount,
    altTextChangeImageCount,
    userTagEditImageCount,
    userTagOutputChangeImageCount,
    hasCropChange: sessionHasCropChange,
    hasRotationChange,
    hasHorizontalFlipChange,
    hasVerticalFlipChange,
    hasThumbnailChange: session.videos.some(
      (video) => video.thumbnailSelection != null,
    ),
    hasTitleChange: session.videos.some(
      (video) => video.title !== video.originalVideo.name,
    ),
  }
}

export const getImageWorkspaceResult = (
  session: ImageWorkspaceSession,
): ImageWorkspaceResultState => {
  const imageIndexes: number[] = []

  session.items.forEach((item, index) => {
    if (
      hasImageGeometryEdit(item.edit) &&
      item.userTagAnchors.some((anchor) => anchor.state === 'invalid')
    ) {
      imageIndexes.push(index)
    }
  })

  if (imageIndexes.length > 0) {
    return {
      state: 'blocked',
      imageIndexes,
    }
  }

  const videoThumbnails = session.videos.flatMap((video) =>
    video.thumbnailSelection != null
      ? [
          {
            video: video.originalVideo,
            offsetMs: video.thumbnailSelection.offsetMs,
            blob: video.thumbnailSelection.blob,
          },
        ]
      : [],
  )
  const videoTitles = session.videos.flatMap((video) =>
    video.title !== video.originalVideo.name
      ? [{ video: video.originalVideo, title: video.title }]
      : [],
  )

  return {
    state: 'ready',
    result: {
      items: session.items.filter(isImageWorkspaceItemDirty).map((item) => ({
        originalImage: item.originalImage,
        edit: cloneImageEdit(item.edit),
        altText: item.altText,
        userTags: getResultUserTags(item),
      })),
      videoThumbnails: videoThumbnails.length > 0 ? videoThumbnails : undefined,
      videoTitles: videoTitles.length > 0 ? videoTitles : undefined,
    },
  }
}
